import React, { useCallback, useEffect, useMemo, useState } from "react";
import { formatISTDate, getISTDateInputValue, getISTDateParts } from "../utils/dateTime";

const LEAVES_URL = "/v1/staff-leaves";
const STAFF_URL = "/v1/clinic/users";
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const getList = (data, keys) => {
    if (Array.isArray(data)) return data;
    for (const key of keys) {
        if (Array.isArray(data?.[key])) return data[key];
    }
    return [];
};

const getErrorMessage = async (response, fallback) => {
    const message = await response.text();
    return message || fallback;
};

const getLeaveDate = (leave) =>
    String(leave.leaveDate || leave.date || "").slice(0, 10);

const getStaffId = (leave) =>
    leave.staffId ??
    leave.staff?.id ??
    leave.staff?.staffId ??
    leave.staff?.userId;

const getUserId = (person) =>
    person.id ?? person.userId ?? person.staffId;

const getStaffName = (staff) => {
    const firstName = staff.firstname ?? staff.firstName ?? "";
    const lastName = staff.lastname ?? staff.lastName ?? "";

    const fullName = `${firstName} ${lastName}`.trim();

    return fullName || staff.name || staff.username || `Staff #${getUserId(staff)}`;
};

const getLeaveStaffName = (leave, person) => {
    const leaveStaff = leave.staff || {};
    const nestedName = [
        leaveStaff.firstname ?? leaveStaff.firstName,
        leaveStaff.lastname ?? leaveStaff.lastName
    ]
        .filter(Boolean)
        .join(" ");

    return (
        leave.staffName ||
        nestedName ||
        leaveStaff.name ||
        (person ? getStaffName(person) : `Staff #${getStaffId(leave) || "?"}`)
    );
};


const getTodayDateString = () => {
    return getISTDateInputValue();
};

function LeaveTrackerModal({ onClose }) {
    const [staff, setStaff] = useState([]);
    const [leaves, setLeaves] = useState([]);
    const [staffId, setStaffId] = useState("");
    const [calendarStaffId, setCalendarStaffId] = useState("");
    const [calendarDate, setCalendarDate] = useState(() => {
        const today = getISTDateParts();
        return new Date(Number(today.year), Number(today.month) - 1, 1);
    });
    const [leaveDate, setLeaveDate] = useState(getTodayDateString);
    const [reason, setReason] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [loadError, setLoadError] = useState("");
    const [submitError, setSubmitError] = useState("");
    const [success, setSuccess] = useState("");

    const year = calendarDate.getFullYear();
    const month = calendarDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstWeekday = new Date(year, month, 1).getDay();
    const calendarCells = useMemo(
        () => [
            ...Array(firstWeekday).fill(null),
            ...Array.from({ length: daysInMonth }, (_, index) => index + 1)
        ],
        [daysInMonth, firstWeekday]
    );
    const staffById = useMemo(
        () => new Map(staff.map((person) => [String(getUserId(person)), person])),
        [staff]
    );
    const selectableStaff = useMemo(
        () => staff.filter((person) => [2, 3].includes(Number(person.role))),
        [staff]
    );
    const leavesByDate = useMemo(
        () =>
            leaves.reduce((groupedLeaves, leave) => {
                const date = getLeaveDate(leave);
                if (!date) return groupedLeaves;
                groupedLeaves[date] = [...(groupedLeaves[date] || []), leave];
                return groupedLeaves;
            }, {}),
        [leaves]
    );

    const loadData = useCallback(async () => {
        setLoading(true);
        setLoadError("");

        try {
            const token = localStorage.getItem("token");
            const headers = { Authorization: `Bearer ${token}` };
            const leaveQuery = new URLSearchParams({
                month: String(month + 1),
                year: String(year)
            });
            if (calendarStaffId) {
                leaveQuery.set("staffId", calendarStaffId);
            }

            const [leavesResponse, staffResponse] = await Promise.all([
                fetch(`${LEAVES_URL}/current-month?${leaveQuery.toString()}`, {
                    headers: { ...headers, Accept: "application/json" }
                }),
                fetch(STAFF_URL, { headers })
            ]);

            if (!leavesResponse.ok) {
                throw new Error(
                    await getErrorMessage(
                        leavesResponse,
                        "Could not load this month's staff leaves."
                    )
                );
            }
            if (!staffResponse.ok) {
                throw new Error(
                    await getErrorMessage(staffResponse, "Could not load staff members.")
                );
            }

            const [leaveData, staffData] = await Promise.all([
                leavesResponse.json(),
                staffResponse.json()
            ]);
            setLeaves(getList(leaveData, ["leaves", "content", "data"]));
            setStaff(getList(staffData, ["users", "content", "data"]));
        } catch (error) {
            console.error(error);
            setLoadError(error.message || "Could not load staff leave information.");
        } finally {
            setLoading(false);
        }
    }, [calendarStaffId, month, year]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const moveCalendarMonth = (offset) => {
        setCalendarDate((currentDate) =>
            new Date(currentDate.getFullYear(), currentDate.getMonth() + offset, 1)
        );
    };

    const showCurrentMonth = () => {
        const today = getISTDateParts();
        setCalendarDate(new Date(Number(today.year), Number(today.month) - 1, 1));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setSubmitError("");
        setSuccess("");

        if (!staffId || !leaveDate || !reason.trim()) {
            setSubmitError("Select a staff member and date, and enter a reason.");
            return;
        }

        setSaving(true);
        try {
            const response = await fetch(LEAVES_URL, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("token")}`
                },
                body: JSON.stringify({
                    staffId: Number(staffId),
                    leaveDate,
                    reason: reason.trim()
                })
            });

            if (!response.ok) {
                throw new Error(
                    await getErrorMessage(response, "Could not save this staff leave.")
                );
            }

            setReason("");
            setSuccess("Staff leave recorded.");
            await loadData();
        } catch (error) {
            console.error(error);
            setSubmitError(error.message || "Could not save this staff leave.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <div className="modal-backdrop fade show" onClick={onClose}></div>
            <div className="modal show fade d-block staff-leave-modal" tabIndex="-1" role="dialog">
                <div className="modal-dialog modal-xl modal-dialog-scrollable">
                    <div className="modal-content">
                        <div className="modal-header">
                            <div>
                                <h5 className="modal-title">Staff Leave Tracker</h5>
                                <div className="text-muted small">
                                    {formatISTDate(`${year}-${String(month + 1).padStart(2, "0")}-01`, {
                                        month: "long",
                                        year: "numeric"
                                    })}
                                </div>
                            </div>
                            <button type="button" className="btn-close" onClick={onClose} aria-label="Close"></button>
                        </div>

                        <div className="modal-body">
                            {loadError && (
                                <div className="alert alert-danger" role="alert">
                                    {loadError}
                                    <button type="button" className="btn btn-sm btn-outline-danger ms-3" onClick={loadData}>
                                        Retry
                                    </button>
                                </div>
                            )}

                            <form className="row g-3 align-items-end mb-4" onSubmit={handleSubmit}>
                                <div className="col-md-4">
                                    <label className="form-label" htmlFor="leave-staff">Staff member</label>
                                    <select
                                        id="leave-staff"
                                        className="form-select"
                                        value={staffId}
                                        onChange={(event) => setStaffId(event.target.value)}
                                        required
                                        disabled={loading || selectableStaff.length === 0}
                                    >
                                        <option value="">Select staff</option>
                                        {selectableStaff.map((person) => (
                                            <option key={getUserId(person)} value={getUserId(person)}>
                                                {getStaffName(person)}
                                            </option>
                                        ))}
                                    </select>
                                    {!loading && !loadError && selectableStaff.length === 0 && (
                                        <div className="form-text">No staff members found.</div>
                                    )}
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label" htmlFor="leave-date">Leave date</label>
                                    <input
                                        id="leave-date"
                                        type="date"
                                        className="form-control"
                                        value={leaveDate}
                                        onChange={(event) => setLeaveDate(event.target.value)}
                                        required
                                    />
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label" htmlFor="leave-reason">Reason</label>
                                    <input
                                        id="leave-reason"
                                        type="text"
                                        className="form-control"
                                        value={reason}
                                        onChange={(event) => setReason(event.target.value)}
                                        placeholder="e.g. Sick leave"
                                        required
                                    />
                                </div>
                                <div className="col-md-2 d-grid">
                                    <button className="btn btn-primary" type="submit" disabled={saving || loading || selectableStaff.length === 0}>
                                        {saving ? "Saving..." : "Record leave"}
                                    </button>
                                </div>
                            </form>

                            {submitError && <div className="alert alert-danger">{submitError}</div>}
                            {success && <div className="alert alert-success">{success}</div>}

                            <div className="d-flex flex-wrap align-items-end justify-content-between gap-2 mb-3">
                                <div className="d-flex align-items-end gap-2">
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        onClick={() => moveCalendarMonth(-1)}
                                        aria-label="Previous month"
                                    >
                                        Previous month
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        onClick={showCurrentMonth}
                                    >
                                        This month
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        onClick={() => moveCalendarMonth(1)}
                                        aria-label="Next month"
                                    >
                                        Next month
                                    </button>
                                </div>
                                <div>
                                    <label className="form-label mb-1" htmlFor="leave-calendar-staff">
                                        Show leaves for
                                    </label>
                                    <select
                                        id="leave-calendar-staff"
                                        className="form-select"
                                        value={calendarStaffId}
                                        onChange={(event) => setCalendarStaffId(event.target.value)}
                                        disabled={loading}
                                    >
                                        <option value="">All staff</option>
                                        {selectableStaff.map((person) => (
                                            <option key={getUserId(person)} value={getUserId(person)}>
                                                {getStaffName(person)}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {loading ? (
                                <div className="text-center py-4">
                                    <div className="spinner-border" role="status"></div>
                                    <div className="mt-2">Loading staff leaves...</div>
                                </div>
                            ) : !loadError ? (
                                <div className="staff-leave-calendar">
                                    {WEEKDAYS.map((day) => (
                                        <div className="staff-leave-weekday" key={day}>{day}</div>
                                    ))}
                                    {calendarCells.map((day, index) => {
                                        if (!day) {
                                            return <div className="staff-leave-day empty" key={`empty-${index}`} />;
                                        }

                                        const date = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                                        const dayLeaves = leavesByDate[date] || [];
                                        return (
                                            <div className={`staff-leave-day${dayLeaves.length ? " has-leave" : ""}`} key={date}>
                                                <span className="staff-leave-day-number">{day}</span>
                                                {dayLeaves.map((leave, leaveIndex) => {
                                                    const person = staffById.get(String(getStaffId(leave)));
                                                    const name = getLeaveStaffName(leave, person);
                                                    return (
                                                        <div
                                                            className="staff-leave-entry"
                                                            key={leave.id || `${date}-${getStaffId(leave)}-${leaveIndex}`}
                                                            title={leave.reason || "Leave"}
                                                        >
                                                            <strong>{name}</strong>
                                                            {leave.reason && <span>{leave.reason}</span>}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : null}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default LeaveTrackerModal;